<template>
  <div>
    <!-- 空状态 -->
    <div v-if="positions.length === 0" style="display:flex;flex-direction:column;align-items:center;padding:40px 0;">
      <var-icon name="badge" size="40" color="#c0c4cc" />
      <span style="margin-top:6px;color:var(--var-text-secondary);font-size:13px;">暂无职位</span>
    </div>

    <!-- 职位表格 -->
    <var-table v-else :scroller-height="'100%'" :elevation="0">
      <thead>
        <tr>
          <th>职位名称</th>
          <th>权限数</th>
          <th style="width: 180px; text-align: center;">操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="pos in positions" :key="pos.id">
          <td>
            <var-icon name="badge" size="18" color="#e6a23c" />
            <span style="margin-left:8px;">{{ pos.name }}</span>
          </td>
          <td>{{ (pos.permissions || []).length }} 项</td>
          <td style="text-align:center;">
            <var-space :size="4">
              <var-button size="small" type="warning" plain @click="openPositionForm(pos)">
                编辑
              </var-button>
              <var-button size="small" type="danger" plain @click="openDeletePositionDialog(pos)">
                删除
              </var-button>
            </var-space>
          </td>
        </tr>
        <tr v-if="positions.length === 0">
          <td :colspan="3">暂无职位</td>
        </tr>
      </tbody>
    </var-table>

    <!-- 职位表单弹窗 -->
    <var-dialog v-model:show="showPositionForm" :title="positionFormMode === 'edit' ? '✏️ 编辑职位' : '➕ 新增职位'"
      width="520px" @confirm="handlePositionSubmit" @close="handleDialogClose" :confirm-loading="submitLoading">

      <div style="padding:8px 0;">
        <!-- 职位名称 -->
        <div style="display:flex;flex-direction:column;gap:4px;margin-bottom:12px;">
          <span style="font-size:13px;color:#666;font-weight:500;">职位名称 <span style="color:#f56c6c;">*</span></span>
          <var-input v-model="positionForm.name" placeholder="请输入职位名称" />
        </div>

        <!-- 权限选择 -->
        <div style="border:1px solid #e8e8e8;border-radius:6px;overflow:hidden;">
          <div style="padding:6px 14px;background:#f7f9fc;border-bottom:1px solid #e8e8e8;">
            <span style="font-size:13px;color:#666;font-weight:500;">权限设置</span>
          </div>
          <div style="padding:4px 8px;">
            <PermissionTreeSelect ref="permissionTreeRef" v-model="positionForm.permissions" mode="normal"
              :disabled="false" />
          </div>
        </div>
      </div>
    </var-dialog>

    <!-- 确认删除职位弹窗 -->
    <var-dialog v-model:show="showDeletePositionDialog" title="⚠️ 确认删除" width="420px"
      @confirm="handleDeletePositionConfirm">
      <div style="padding:8px 0;">
        <p style="margin-bottom:8px;">确定要删除职位 <strong>{{ deletePositionTarget?.name }}</strong> 吗？</p>
        <p style="color:#f56c6c;font-size:13px;">⚠️ 已分配该职位的用户将保留权限但职位变为空</p>
      </div>
    </var-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { Snackbar } from '@varlet/ui'
import { userApi } from '@/api'
import PermissionTreeSelect from '@/components/common/PermissionTreeSelect.vue'

const positions = ref([])
const permissionTreeRef = ref(null)
const submitLoading = ref(false)

// ===== 职位表单 =====
const showPositionForm = ref(false)
const positionFormMode = ref('add')
const positionForm = ref({
  id: null,
  name: '',
  permissions: [],
})

// ===== 删除 =====
const showDeletePositionDialog = ref(false)
const deletePositionTarget = ref(null)

// ===== 加载数据 =====
async function loadPositions() {
  try {
    const data = await userApi.getPositions()
    if (data?.success) {
      positions.value = data.data || []
    }
  } catch (err) {
    console.error('[PositionManagement] 加载职位失败:', err)
    Snackbar.error('加载职位失败')
  }
}

// ===== 职位操作 =====
function openPositionForm(pos = null) {
  if (pos) {
    positionFormMode.value = 'edit'
    positionForm.value = {
      id: pos.id,
      name: pos.name,
      permissions: pos.permissions || [],
    }
  } else {
    positionFormMode.value = 'add'
    positionForm.value = {
      id: null,
      name: '',
      permissions: [],
    }
  }
  showPositionForm.value = true

  nextTick(() => {
    if (permissionTreeRef.value && positionForm.value.permissions.length > 0) {
      permissionTreeRef.value.setSelectedValues(positionForm.value.permissions)
    }
  })
}

// ===== 弹窗关闭时重置状态 =====
const handleDialogClose = () => {
  positionForm.value = {
    id: null,
    name: '',
    permissions: [],
  }
}

async function handlePositionSubmit() {
  if (!positionForm.value.name?.trim()) {
    Snackbar.error('请输入职位名称')
    return
  }

  const data = {
    name: positionForm.value.name.trim(),
    permissions: positionForm.value.permissions || [],
  }

  submitLoading.value = true

  try {
    let result
    if (positionFormMode.value === 'edit') {
      data.id = positionForm.value.id
      result = await userApi.updatePosition(data.id, data.name, data.permissions)
    } else {
      result = await userApi.createPosition(data.name, data.permissions)
    }

    if (result?.success) {
      Snackbar.success(positionFormMode.value === 'edit' ? '职位更新成功' : '职位创建成功')
      showPositionForm.value = false
      await loadPositions()
    } else {
      Snackbar.error(result?.msg || '操作失败')
    }
  } catch (err) {
    console.error('[PositionManagement] 职位操作失败:', err)
    Snackbar.error('操作失败: ' + err.message)
  } finally {
    submitLoading.value = false
  }
}

function openDeletePositionDialog(pos) {
  deletePositionTarget.value = pos
  showDeletePositionDialog.value = true
}

async function handleDeletePositionConfirm() {
  if (!deletePositionTarget.value) return
  try {
    const result = await userApi.deletePosition(deletePositionTarget.value.id)
    if (result?.success) {
      Snackbar.success('职位已删除')
      showDeletePositionDialog.value = false
      deletePositionTarget.value = null
      await loadPositions()
    } else {
      Snackbar.error(result?.msg || '删除失败')
    }
  } catch (err) {
    console.error('[PositionManagement] 删除职位失败:', err)
    Snackbar.error('删除失败: ' + err.message)
  }
}

// ===== 暴露给父组件 =====
defineExpose({
  loadPositions,
  openPositionForm,
})

onMounted(() => {
  loadPositions()
})
</script>